import { Component, OnInit } from '@angular/core';
import { DataServiceService } from '../services/data-service.service';
import { DriverPreferencesService } from '../services/driver-preferences.service';
import { Drivers } from '../interfaces';
import { MessageService } from 'primeng/api';

interface DriverWithSelection extends Drivers {
  selected?: boolean;
}

@Component({
  selector: 'app-manage-drivers',
  templateUrl: './manage-drivers.component.html',
  styleUrls: ['./manage-drivers.component.scss']
})
export class ManageDriversComponent implements OnInit {
  drivers: DriverWithSelection[] = [];
  selectedDriverIds: number[] = [];
  otherDrivers: DriverWithSelection[] = [];

  driverTypes: Array<{
    type: 'Conveyor' | 'Dump' | 'Hopper' | 'Pneumatic';
    drivers: DriverWithSelection[];
    className: string;
  }> = [
    { type: 'Conveyor', drivers: [] as DriverWithSelection[], className: 'conveyor-section' },
    { type: 'Dump', drivers: [] as DriverWithSelection[], className: 'dump-section' },
    { type: 'Hopper', drivers: [] as DriverWithSelection[], className: 'hopper-section' },
    { type: 'Pneumatic', drivers: [] as DriverWithSelection[], className: 'pneumatic-section' }
  ];
  
  constructor(
    private dataService: DataServiceService,
    private driverPreferencesService: DriverPreferencesService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    // Load selected driver IDs first
    this.driverPreferencesService.selectedDriverIds$.subscribe(ids => {
      this.selectedDriverIds = ids;
      
      // Update selected property for all drivers if we have drivers loaded
      if (this.drivers.length > 0) {
        this.updateDriverSelectionState();
      }
    });

    // Load all drivers
    this.dataService.drivers$.subscribe(drivers => {
      // Convert to DriverWithSelection and set selected property
      this.drivers = drivers.map(driver => {
        const driverWithSelection: DriverWithSelection = { ...driver };
        driverWithSelection.selected = this.isDriverSelected(driver.driver_id);
        return driverWithSelection;
      });
      
      // Update each driver type's drivers array
      this.driverTypes.forEach(driverType => {
        driverType.drivers = this.drivers.filter(driver => driver.type === driverType.type);
      });
      
      // Update other drivers
      this.otherDrivers = this.drivers.filter(driver => 
        !this.driverTypes.some(type => type.type === driver.type));
    });

    // Initial load of drivers if not already loaded
    if (this.drivers.length === 0) {
      this.dataService.getDriverDetails().subscribe();
    }
  }

  private updateDriverSelectionState(): void {
    this.drivers.forEach(driver => {
      const isSelected = this.isDriverSelected(driver.driver_id);
      driver.selected = isSelected;
    });
  }


  // Get drivers without a type
  getOtherDrivers(): DriverWithSelection[] {
    return this.drivers.filter(driver => !driver.type);
  }

  // Toggle selection of a single driver
  toggleDriver(driver: DriverWithSelection): void {
    // Call toggleDriver only once
    this.driverPreferencesService.toggleDriver(driver.driver_id);
    
    // Update the selected state after toggling
    driver.selected = this.isDriverSelected(driver.driver_id);
    
    // Show appropriate toast message
    this.showToast(`${driver.first_name} ${driver.last_name} ${driver.selected ? 'added to' : 'removed from'} dashboard`);
  }


  // Check if a driver is selected
  isDriverSelected(driverId: number): boolean {
    return this.selectedDriverIds.includes(driverId);
  }

  selectAllDriversByType(type: 'Pneumatic' | 'Dump' | 'Hopper' | 'Conveyor'): void {
    this.driverPreferencesService.selectDriversByType(this.drivers, type);
    this.showToast(`All ${type} drivers added to dashboard`);
  }

  deselectAllDriversByType(type: 'Pneumatic' | 'Dump' | 'Hopper' | 'Conveyor'): void {
    this.driverPreferencesService.deselectDriversByType(this.drivers, type);
    this.showToast(`All ${type} drivers removed from dashboard`);
  }

  // Select all pneumatic drivers
  selectAllPneumaticDrivers(): void {
    this.driverPreferencesService.selectDriversByType(this.drivers, 'Pneumatic');
    this.showToast('All pneumatic drivers added to dashboard');
  }


  // // Deselect all pneumatic drivers
  // deselectAllPneumaticDrivers(): void {
  //   this.driverPreferencesService.deselectDriversByType(this.drivers, 'Pneumatic');
  //   this.showToast('All pneumatic drivers removed from dashboard');
  // }

  // // Select all dump drivers
  // selectAllDumpDrivers(): void {
  //   this.driverPreferencesService.selectDriversByType(this.drivers, 'Dump');
  //   this.showToast('All dump drivers added to dashboard');
  // }

  // // Deselect all dump drivers
  // deselectAllDumpDrivers(): void {
  //   this.driverPreferencesService.deselectDriversByType(this.drivers, 'Dump');
  //   this.showToast('All dump drivers removed from dashboard');
  // }

  // Select all drivers
  selectAllDrivers(): void {
    this.driverPreferencesService.selectAllDrivers(this.drivers);
    this.showToast('All drivers added to dashboard');
  }

  // Deselect all drivers
  deselectAllDrivers(): void {
    this.driverPreferencesService.deselectAllDrivers();
    this.showToast('All drivers removed from dashboard');
  }

  // // Select all other drivers
  // selectAllOtherDrivers(): void {
  //   // Select drivers that are neither Pneumatic nor Dump
  //   this.otherDrivers.forEach(driver => {
  //     if (!driver.selected) {
  //       this.driverPreferencesService.toggleDriver(driver.driver_id);
  //       driver.selected = true;
  //     }
  //   });
  //   this.showToast('All other drivers added to dashboard');
  // }

  // // Deselect all other drivers
  // deselectAllOtherDrivers(): void {
  //   // Deselect drivers that are neither Pneumatic nor Dump
  //   this.otherDrivers.forEach(driver => {
  //     if (driver.selected) {
  //       this.driverPreferencesService.toggleDriver(driver.driver_id);
  //       driver.selected = false;
  //     }
  //   });
  //   this.showToast('All other drivers removed from dashboard');
  // }

  private showToast(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }
}
